import * as React from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import {
  Button,
  Checkbox,
  FormField,
  Input,
  OtpInput,
  useToast,
} from "@chotca/ui";
import { vi, EmployerAnalyticsEvent, LEGAL_COMPANY as C } from "@chotca/config";
import { getSdk, useMe } from "@chotca/api-client";
import { logAnalyticsEvent } from "@/lib/analytics";
import { AppBootLoader } from "@chotca/app-shell";
import { setAccessToken } from "@/boot/access-token";
import { AuthLayout } from "./auth-layout";
import { errMsg } from "./auth-errors";
import { readRedirectParam, resolvePostLoginUrl } from "./post-login";

const RESEND_SECONDS = 60;
const OTP_LENGTH = 6;

type Step =
  | { kind: "form" }
  | { kind: "otp" }
  | { kind: "done"; redirectUrl: string };

export function RegisterPage() {
  const t = useTranslations("auth");
  const { error: toastError, success: toastSuccess } = useToast();
  const me = useMe();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [redirect] = React.useState(() => readRedirectParam());

  const [step, setStep] = React.useState<Step>({ kind: "form" });
  const [creds, setCreds] = React.useState({
    fullName: "",
    email: "",
    password: "",
  });
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  // LEGAL-08: must be ticked before registerStart/registerComplete are called —
  // the server re-validates this flag independently (z.literal(true)).
  const [accepted, setAccepted] = React.useState(false);

  if (me.isLoading) return <AppBootLoader />;
  if (me.data) {
    return (
      <Navigate
        to={resolvePostLoginUrl({
          globalRole: me.data.globalRole,
          hasMembership: me.data.memberships.length > 0,
          redirect,
        })}
        replace
      />
    );
  }

  async function onStartSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    setError(null);
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") ?? "").trim();
    const password = String(fd.get("password") ?? "");
    const fullNameRaw = String(fd.get("fullName") ?? "").trim();
    const fullName =
      fullNameRaw.length > 0 ? fullNameRaw : email.split("@")[0]!.slice(0, 120);
    try {
      await getSdk().auth.registerStart({
        email,
        password,
        fullName,
        consentAccepted: true,
      });
      setCreds({ fullName, email, password });
      setStep({ kind: "otp" });
    } catch (err) {
      const msg = errMsg(err, "Đăng ký thất bại");
      setError(msg);
      toastError(msg);
    } finally {
      setPending(false);
    }
  }

  async function onVerify(code: string) {
    setPending(true);
    setError(null);
    try {
      const res = await getSdk().auth.registerComplete({
        email: creds.email,
        password: creds.password,
        fullName: creds.fullName,
        code,
        consentAccepted: true,
      });
      setAccessToken(res.accessToken);
      qc.setQueryData(["me"], res.user);
      logAnalyticsEvent(EmployerAnalyticsEvent.employerSignup);
      const url = resolvePostLoginUrl({
        globalRole: res.user.globalRole,
        hasMembership: res.user.memberships.length > 0,
        redirect,
      });
      setStep({ kind: "done", redirectUrl: url });
      if (url.startsWith("/")) {
        nav(url, { replace: true });
      } else {
        window.location.assign(url);
      }
    } catch (err) {
      const msg = errMsg(err, t("registerOtpInvalid"));
      setError(msg);
      toastError(msg);
      setPending(false);
    }
  }

  async function onResend() {
    setError(null);
    try {
      await getSdk().auth.registerStart({
        email: creds.email,
        password: creds.password,
        fullName: creds.fullName,
        consentAccepted: true,
      });
      toastSuccess(t("registerOtpResent"));
      return true;
    } catch (err) {
      const msg = errMsg(err, "Không gửi lại được mã");
      setError(msg);
      toastError(msg);
      return false;
    }
  }

  if (step.kind === "done") {
    return (
      <AuthLayout label={vi.landing.employerCta} title={t("registerTitle")}>
        <div className="flex flex-col items-center text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-lime/20">
            <CheckCircle2
              className="h-9 w-9 text-lime-dark"
              strokeWidth={2.2}
            />
          </div>
          <h2 className="mt-5 font-satoshi text-2xl font-black tracking-tight">
            {t("registerSuccessTitle")}
          </h2>
          <p className="mt-2 max-w-sm text-base text-ink/70">
            {t("registerSuccessSubtitle")}
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (step.kind === "otp") {
    return (
      <AuthLayout
        label={t("registerOtpTitle")}
        title={t("registerOtpTitle")}
        subtitle={t("registerOtpSubtitle")}
      >
        <OtpStep
          email={creds.email}
          pending={pending}
          error={error}
          onChangeEmail={() => {
            setError(null);
            setStep({ kind: "form" });
          }}
          onVerify={onVerify}
          onResend={onResend}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      label={vi.landing.employerCta}
      title={t("registerTitle")}
      subtitle={t("registerSubtitle")}
      footer={
        <Link
          to="/login"
          className="font-bold text-ink underline-offset-4 hover:underline"
        >
          {t("loginLink")}
        </Link>
      }
    >
      <form onSubmit={onStartSubmit} className="space-y-4">
        <FormField label={t("fullNameLabel")}>
          <Input
            name="fullName"
            type="text"
            autoComplete="name"
            required
            autoFocus
            defaultValue={creds.fullName}
          />
        </FormField>
        <FormField label={t("emailLabel")}>
          <Input
            name="email"
            type="email"
            autoComplete="email"
            required
            defaultValue={creds.email}
          />
        </FormField>
        <FormField
          label={t("passwordLabel")}
          description={t("passwordTooWeak")}
        >
          <Input
            name="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={8}
            defaultValue={creds.password}
          />
        </FormField>

        {error ? (
          <p className="rounded-xl bg-danger/10 p-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        {/* LEGAL-08: explicit, unchecked-by-default consent gate — server
            re-validates independently. Platform-rules is a read-only link on
            this checkbox, not a separate consent purpose. */}
        <Checkbox
          checked={accepted}
          onChange={(e) => setAccepted(e.target.checked)}
          label={
            <span>
              {t("termsAgreePrefix")}{" "}
              <a
                href={`${C.websiteUrl}/terms`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-lime-dark underline"
              >
                {t("termsLinkTerms")}
              </a>
              {", "}
              <a
                href={`${C.websiteUrl}/employer-agreement`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-lime-dark underline"
              >
                {t("termsLinkEmployerAgreement")}
              </a>
              {" và "}
              <a
                href={`${C.websiteUrl}/platform-rules`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-lime-dark underline"
              >
                {t("termsLinkPlatformRules")}
              </a>
              .
            </span>
          }
        />
        <p className="text-xs text-ink/60">{t("privacyNotice")}</p>

        <Button
          type="submit"
          fullWidth
          loading={pending}
          size="lg"
          disabled={!accepted}
        >
          {pending ? t("registering") : t("registerCta")}
        </Button>
      </form>
    </AuthLayout>
  );
}

function OtpStep({
  email,
  pending,
  error,
  onChangeEmail,
  onVerify,
  onResend,
}: {
  email: string;
  pending: boolean;
  error: string | null;
  onChangeEmail: () => void;
  onVerify: (code: string) => void;
  onResend: () => Promise<boolean>;
}) {
  const t = useTranslations("auth");
  const [code, setCode] = React.useState("");
  const [secondsLeft, setSecondsLeft] = React.useState(RESEND_SECONDS);
  const [resending, setResending] = React.useState(false);

  React.useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = window.setInterval(() => {
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft]);

  function submitCode(value: string) {
    if (value.length === OTP_LENGTH && !pending) onVerify(value);
  }

  async function handleResend() {
    setResending(true);
    const ok = await onResend();
    setResending(false);
    if (ok) {
      setCode("");
      setSecondsLeft(RESEND_SECONDS);
    }
  }

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3 rounded-xl bg-mist px-4 py-3 text-sm">
        <span className="truncate">
          {t("registerOtpSentTo")} <span className="font-bold">{email}</span>
        </span>
        <button
          type="button"
          onClick={onChangeEmail}
          className="shrink-0 font-bold text-lime-dark hover:underline"
        >
          {t("registerOtpChangeEmail")}
        </button>
      </div>

      <OtpInput
        length={OTP_LENGTH}
        value={code}
        onChange={setCode}
        onComplete={submitCode}
        disabled={pending}
        invalid={!!error}
        autoFocus
      />

      {error ? (
        <p className="rounded-xl bg-danger/10 p-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <p className="text-center text-sm text-ash">
        {secondsLeft > 0 ? (
          t("registerOtpResendIn", { seconds: `${mm}:${ss}` })
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={resending}
            className="font-bold text-lime-dark hover:underline disabled:opacity-60"
          >
            {resending ? t("registerOtpResending") : t("registerOtpResend")}
          </button>
        )}
      </p>

      <Button
        type="button"
        fullWidth
        size="lg"
        loading={pending}
        disabled={code.length !== OTP_LENGTH}
        onClick={() => submitCode(code)}
      >
        {pending ? t("registerOtpVerifying") : t("registerOtpCta")}
      </Button>
      <Button type="button" variant="ghost" fullWidth onClick={onChangeEmail}>
        {t("registerOtpBack")}
      </Button>
    </div>
  );
}
